import { toPng } from "html-to-image"
import { pdf } from "@react-pdf/renderer"

import { GanttPdfDocument } from "./GanttPdfDocument.tsx"
import {
  AVAILABLE_ROW_HEIGHT_PT,
  IMAGE_GAP_PT,
  USABLE_WIDTH_PT,
  type GanttPdfLayoutResult
} from "./gantt-pdf-constants.ts"

export interface GanttPdfResult {
  blob: Blob
  scaledDown: boolean
}

interface CapturedNode {
  dataUrl: string
  width: number
  height: number
}

async function captureNode(node: HTMLElement): Promise<CapturedNode> {
  const dataUrl = await toPng(node, { pixelRatio: 2, backgroundColor: "#ffffff" })
  return { dataUrl, width: node.scrollWidth, height: node.scrollHeight }
}

// Both images are scaled by one shared factor, and both get their final
// height from one shared natural height (never each one's own) — the two
// panels share an on-screen `rowHeight`, so this is what keeps milestone
// row N's label aligned with row N's bar. The factor is capped at 1 so a
// small project is never stretched blurry past its 2x-pixelRatio capture.
export function computeGanttPdfImageLayout(
  leftPanel: { width: number; height: number },
  chart: { width: number; height: number }
): GanttPdfLayoutResult {
  if (leftPanel.width <= 0 || leftPanel.height <= 0 || chart.width <= 0 || chart.height <= 0) {
    // A capture with no real size (briefly hidden, mid-transition) would
    // otherwise divide into NaN/Infinity and flow into <Image> as an
    // invalid style with no visible error.
    throw new Error("Cannot lay out an empty Gantt capture")
  }

  const scaleToFitWidth = (USABLE_WIDTH_PT - IMAGE_GAP_PT) / (leftPanel.width + chart.width)
  const naturalHeight = Math.max(leftPanel.height, chart.height)
  const scaleToFitHeight = AVAILABLE_ROW_HEIGHT_PT / naturalHeight
  const scale = Math.min(scaleToFitWidth, scaleToFitHeight, 1)

  return {
    layout: {
      leftPanelWidth: leftPanel.width * scale,
      leftPanelHeight: naturalHeight * scale,
      chartWidth: chart.width * scale,
      chartHeight: naturalHeight * scale
    },
    scaledDown: scale < 1
  }
}

export async function buildGanttPdfBlob({
  leftPanelEl,
  chartEl,
  projectName,
  generatedAtLabel
}: {
  leftPanelEl: HTMLElement
  chartEl: HTMLElement
  projectName?: string
  // Pre-formatted by the caller's own `useDateFormatter` -- @react-pdf/renderer
  // builds its document via a separate renderer instance the app's Redux
  // store never reaches, so the account's date format can't be resolved here.
  generatedAtLabel: string
}): Promise<GanttPdfResult> {
  const [leftPanel, chart] = await Promise.all([
    captureNode(leftPanelEl),
    captureNode(chartEl)
  ])

  const { layout, scaledDown } = computeGanttPdfImageLayout(leftPanel, chart)

  const blob = await pdf(
    <GanttPdfDocument
      projectName={projectName}
      generatedAtLabel={generatedAtLabel}
      leftPanelDataUrl={leftPanel.dataUrl}
      chartDataUrl={chart.dataUrl}
      layout={layout}
    />
  ).toBlob()

  return { blob, scaledDown }
}
